import React, { useState, useEffect } from 'react';
import API_BASE from './apiBase';

const DEFAULT_FAQS = [
  {
    id: 1,
    question: "How long does it take to process my request?",
    answer: "Under the DPDP Act 2023, we are required to respond to your request within 30 days. We strive to complete most requests within 15 days."
  },
  {
    id: 2,
    question: "How will I receive my data?",
    answer: "You will receive your data via secure email in a machine-readable format (typically JSON or CSV). For large datasets, we may provide a secure download link."
  },
  {
    id: 3,
    question: "Can I request deletion of all my data?",
    answer: "Yes, you can request erasure of your data. However, we may need to retain certain data for legal or regulatory compliance purposes."
  }
];

const Help = ({ tenantId }) => {
  const [openIndex, setOpenIndex] = useState(0);
  const [showAllFaqs, setShowAllFaqs] = useState(false);

  const [faqs, setFaqs] = useState([]);
  const [uploadedFaqCount, setUploadedFaqCount] = useState(0);
  const [dpoContact, setDpoContact] = useState(null);
  
  useEffect(() => {
    // Fetch FAQs
    const fetchFaqs = async () => {
      const resolvedTenantId = tenantId || window.USER?.tenant_id || localStorage.getItem('tenant_id');
      if (!resolvedTenantId) {
        setFaqs(DEFAULT_FAQS);
        setUploadedFaqCount(0);
        return;
      }
      try {
        const res = await fetch(`${API_BASE}/manager/faq?tenant_id=${encodeURIComponent(resolvedTenantId)}`);
        if (!res.ok) {
          setFaqs(DEFAULT_FAQS);
          setUploadedFaqCount(0);
          return;
        }
        const data = await res.json();
        const activeFaqs = Array.isArray(data)
          ? data.filter(faq => faq && faq.active !== false && faq.question && faq.answer)
          : [];
        setUploadedFaqCount(activeFaqs.length);
        if (activeFaqs.length > 0) setFaqs(activeFaqs);
        else setFaqs(DEFAULT_FAQS);
      } catch (err) {
        console.debug('Failed to load FAQs', err);
        setFaqs(DEFAULT_FAQS);
        setUploadedFaqCount(0);
      }
    };

    // Fetch DPO Contact Info
    const fetchDpoContact = async () => {
      try {
        const res = await fetch(`${API_BASE}/manager/help/dpo-contact`);
        if (!res.ok) return;
        const data = await res.json();
        if (data && Object.keys(data).length) {
          setDpoContact(data);
        }
      } catch (err) {
        console.debug('Failed to load DPO contact', err);
      }
    };

    fetchFaqs();
    fetchDpoContact();
  }, [tenantId]);

  const toggleAccordion = (index) => {
    setOpenIndex(openIndex === index ? -1 : index);
  };

  return (
    <div className="help-support-container">
      {/* Page Header */}
      <div className="help-header">
        <div className="help-header-content">
          <div>
            <h1>Help & Support</h1>
            <p className="help-subtitle">We're here to assist you with your data privacy questions</p>
          </div>
          <button type="button" className="help-back-btn" onClick={() => window.history.back()}>
            ← Back
          </button>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="help-content-grid">
        
        {/* LEFT COLUMN - FAQ */}
        <div className="help-faq-section">
          <h2>Frequently Asked Questions{uploadedFaqCount > 0 ? ` (${uploadedFaqCount})` : ''}</h2>
          
          <div className="faq-accordion-list">
            {(showAllFaqs ? faqs : faqs.slice(0, 10)).map((faq, index) => (
              <div 
                key={faq.id} 
                className={`faq-accordion-item ${openIndex === index ? 'active' : ''}`}
              >
                <div 
                  className="faq-accordion-header"
                  onClick={() => toggleAccordion(index)}
                >
                  <h3 className="faq-accordion-question">
                    {faq.question}
                  </h3>
                  <span className="faq-accordion-icon">
                    {openIndex === index ? '−' : '+'}
                  </span>
                </div>
                {openIndex === index && (
                  <div className="faq-accordion-answer">
                    <p>{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
          
          {faqs.length > 10 && (
            <div className="faq-view-toggle">
              <button
                type="button"
                className="faq-toggle-btn"
                onClick={() => setShowAllFaqs(!showAllFaqs)}
              >
                {showAllFaqs ? 'Show Less' : `Show ${faqs.length - 10} More`}
              </button>
            </div>
          )}
        </div>

        {/* RIGHT COLUMN - Contact & Question */}
        <div className="help-sidebar">
          
          {/* DPO Contact Information */}
          <div className="dpo-contact-card">
            <div className="card-header">
              <svg className="card-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <h3>Data Protection Officer</h3>
            </div>
            
            {dpoContact ? (
              <div className="dpo-info">
                {dpoContact.fullName && (
                  <div className="info-item">
                    <span className="info-label">Name</span>
                    <span className="info-value">{dpoContact.fullName}</span>
                  </div>
                )}
                {dpoContact.email && (
                  <div className="info-item">
                    <span className="info-label">Email</span>
                    <a href={`mailto:${dpoContact.email}`} className="info-value info-link">
                      {dpoContact.email}
                    </a>
                  </div>
                )}
                {dpoContact.phone && (
                  <div className="info-item">
                    <span className="info-label">Phone</span>
                    <a href={`tel:${dpoContact.phone}`} className="info-value info-link">
                      {dpoContact.phone}
                    </a>
                  </div>
                )}
                {dpoContact.address && (
                  <div className="info-item">
                    <span className="info-label">Address</span>
                    <span className="info-value">{dpoContact.address}</span>
                  </div>
                )}
                {dpoContact.responseTime && (
                  <div className="info-item">
                    <span className="info-label">Response Time</span>
                    <span className="info-value">{dpoContact.responseTime}</span>
                  </div>
                )}
              </div>
            ) : (
              <p className="no-contact-message">Contact information not available</p>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default Help;